import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { motion } from "framer-motion";
import axios from "axios";

const COLORS = ["#6366F1", "#8B5CF6", "#EC4899", "#10B981", "#F59E0B"];

type StatusDef = { id: number; name: string };
/* eslint-disable camelcase */
type Order = { status_id: number };
/* eslint-enable camelcase */
type SlicePoint = { name: string; value: number };

type Props = { jwtToken: string };

export default function OrderStatusChart({ jwtToken }: Props) {
  const [data, setData] = useState<SlicePoint[]>([]);

  useEffect(() => {
    if (!jwtToken) return;
    const headers = { Authorization: `Bearer ${jwtToken}` };

    Promise.all([
      axios.get("/api/v1/orderStatuses", { headers }),
      axios.get("/api/v1/orders", { headers }),
    ])
      .then(([statusRes, orderRes]) => {
        const statuses: StatusDef[] = statusRes.data.data ?? [];
        const orders: Order[] = orderRes.data.data ?? [];

        const counts: Record<number, number> = {};
        /* eslint-disable camelcase */
        orders.forEach((order) => {
          counts[order.status_id] = (counts[order.status_id] ?? 0) + 1;
        });
        /* eslint-enable camelcase */

        const slices = statuses
          .map((s) => ({ name: s.name, value: counts[s.id] ?? 0 }))
          .filter((s) => s.value > 0);

        setData(slices);
      })
      .catch(() => {});
  }, [jwtToken]);

  return (
    <motion.div
      className="bg-gray-800 bg-opacity-50 backdrop-blur-md shadow-lg rounded-xl p-6 border border-gray-700"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <h2 className="text-lg font-medium mb-4 text-gray-100">
        Orders by Status
      </h2>
      {data.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-gray-500 text-sm">
          No order data yet
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {data.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={COLORS[data.indexOf(entry) % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(31,41,55,0.8)",
                  borderColor: "#4B5563",
                }}
                itemStyle={{ color: "#E5E7EB" }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}
